/**
 * Centralized navigation utility for all link handling in the widget.
 *
 * Production behavior: same-origin links navigate the parent page in-place;
 * cross-origin links open in a new tab.
 *
 * Demo behavior (promptmysite.com): always opens in a new tab and shows a
 * brief toast explaining that on the real site the user would navigate
 * in the same page.
 */

const DEMO_HOSTS = ['promptmysite.com', 'www.promptmysite.com', 'localhost'];

let _isDemo: boolean | null = null;

/**
 * Check whether the widget is running on a demo/showcase host
 * (promptmysite.com or localhost). Cached after first call.
 */
function isDemo(): boolean {
  if (_isDemo !== null) return _isDemo;
  try {
    const host = (window.top?.location.hostname || window.location.hostname).toLowerCase();
    _isDemo = DEMO_HOSTS.some(d => host === d || host.endsWith('.' + d));
  } catch {
    // Cross-origin top frame — we're embedded, not on demo
    _isDemo = false;
  }
  return _isDemo;
}

/**
 * Navigate to a URL using production-appropriate behavior.
 *
 * - **Demo site**: opens in new tab, shows explanatory toast.
 * - **Production (same-origin)**: navigates parent page in-place.
 * - **Production (cross-origin)**: opens in a new tab.
 *
 * @param url      The URL to navigate to
 * @param toastRoot Optional shadow root or element to append demo toast to
 */
export function navigateToUrl(url: string, toastRoot?: HTMLElement | ShadowRoot | null): void {
  // Always open in a new tab so the current page (and chat) stays intact.
  try {
    const resolved = new URL(url, window.location.origin).href;
    window.open(resolved, '_blank', 'noopener');
  } catch {
    window.open(url, '_blank', 'noopener');
  }

  if (isDemo() && toastRoot) {
    showDemoToast(toastRoot);
  }
}

/**
 * Install a delegated click interceptor on a shadow root (or container)
 * that catches `<a target="_blank">` clicks and routes them through
 * `navigateToUrl` instead of the browser default.
 */
export function installLinkInterceptor(root: ShadowRoot | HTMLElement): void {
  root.addEventListener('click', (e: Event) => {
    const anchor = (e.target as HTMLElement).closest?.('a[target="_blank"]') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || href === '#') return;

    e.preventDefault();
    e.stopPropagation();
    navigateToUrl(href, root instanceof ShadowRoot ? root : null);
  });
}

// ── Demo toast ──────────────────────────────────────────────────────────────

let toastTimeout: ReturnType<typeof setTimeout> | null = null;

function showDemoToast(root: HTMLElement | ShadowRoot): void {
  // Avoid stacking multiple toasts
  const existing = root.querySelector?.('.pms-demo-nav-toast') as HTMLElement | null;
  if (existing) {
    existing.remove();
    if (toastTimeout) clearTimeout(toastTimeout);
  }

  const toast = document.createElement('div');
  toast.className = 'pms-demo-nav-toast';
  toast.textContent = 'On your site, this would navigate in the same page.';

  // Inject minimal styles inline (works inside shadow DOM)
  toast.style.cssText = `
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(30, 30, 60, 0.95);
    color: #cbd5e1;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    z-index: 999999;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    white-space: nowrap;
    border: 1px solid rgba(255,255,255,0.08);
  `;

  (root instanceof ShadowRoot ? root : root).appendChild(toast);

  // Fade in
  requestAnimationFrame(() => { toast.style.opacity = '1'; });

  // Fade out and remove
  toastTimeout = setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 200);
  }, 2500);
}
